// src/components/WlasnoscIntelektualna.js

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Shield, FileText, Award, Lock, Eye, Database, CheckCircle, ArrowRight, ChevronDown, ChevronUp } from 'lucide-react';
import Trust from './Trust';
import ContactForm from './ContactForm';

// Add font import
const fontLink = document.createElement('link');
fontLink.href = 'https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap';
fontLink.rel = 'stylesheet';
document.head.appendChild(fontLink);

const WlasnoscIntelektualna = () => {
  const [openFaq, setOpenFaq] = useState(null);

  const intellectualPropertyServices = [
    {
      icon: <FileText className="w-8 h-8" />,
      title: "Prawo autorskie",
      description: "Ochrona dzieł (oprogramowania, stron internetowych, treści, grafiki), umowy licencyjne, przeniesienie praw autorskich."
    },
    {
      icon: <Award className="w-8 h-8" />,
      title: "Znaki towarowe",
      description: "Rejestracja znaków towarowych (krajowych, unijnych, międzynarodowych), ochrona przed naruszeniami, reprezentacja w sporach."
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Patenty i wzory przemysłowe",
      description: "Doradztwo w procesie zgłoszenia i ochrony wynalazków oraz wzorów."
    },
    {
      icon: <Lock className="w-8 h-8" />,
      title: "Tajemnica przedsiębiorstwa i know-how",
      description: "Opracowywanie wewnętrznych regulacji, umowy o zachowaniu poufności (NDA)."
    },
    {
      icon: <Eye className="w-8 h-8" />,
      title: "Zwalczanie nieuczciwej konkurencji",
      description: "Działania przeciwko naśladowcom i naruszeniom dóbr niematerialnych."
    }
  ];

  const rodoServices = [
    {
      icon: <Database className="w-8 h-8" />,
      title: "Audyty zgodności z RODO",
      description: "Analiza procesów przetwarzania danych w firmie i wskazanie obszarów wymagających poprawy."
    },
    {
      icon: <FileText className="w-8 h-8" />,
      title: "Przygotowanie dokumentacji RODO",
      description: "Polityki prywatności, klauzule informacyjne, umowy powierzenia przetwarzania danych."
    },
    {
      icon: <Award className="w-8 h-8" />,
      title: "Szkolenia RODO",
      description: "Dla pracowników i kadry zarządzającej."
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Obsługa incydentów",
      description: "Wsparcie w przypadku naruszeń ochrony danych osobowych."
    },
    {
      icon: <Lock className="w-8 h-8" />,
      title: "Reprezentacja przed UODO",
      description: "W kontaktach z Prezesem Urzędu Ochrony Danych Osobowych."
    }
  ];

  const faqs = [
    {
      question: "Czy muszę rejestrować swój znak towarowy?",
      answer: "Rejestracja znaku towarowego nie jest obowiązkowa, ale jest wysoce zalecana. Zapewnia wyłączność używania znaku na danym terytorium i ułatwia dochodzenie roszczeń w przypadku jego naruszenia."
    },
    {
      question: "Jakie są kluczowe obowiązki wynikające z RODO dla mojej firmy?",
      answer: "Główne obowiązki to m.in.: zasada minimalizacji danych, ich prawidłowe zabezpieczenie, informowanie osób o przetwarzaniu ich danych, uzyskiwanie zgód (jeśli wymagane), prowadzenie rejestru czynności przetwarzania oraz wyznaczanie Inspektora Ochrony Danych (IOD), jeśli jest to konieczne."
    },
    {
      question: "Co to jest tajemnica przedsiębiorstwa i jak ją chronić?",
      answer: "Tajemnica przedsiębiorstwa to nieujawnione informacje o wartości gospodarczej, techniczne, technologiczne lub organizacyjne, co do których przedsiębiorca podjął niezbędne działania w celu zachowania ich poufności. Chronić ją można poprzez odpowiednie umowy z pracownikami (NDA), regulaminy wewnętrzne, zabezpieczenia techniczne i procesowe."
    }
  ];

  const handleContactScroll = () => {
    const contactSection = document.querySelector('#kontakt');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-black text-white pt-20">
      {/* Hero Section */}
      <section className="py-16 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 right-20 animate-float">
            <Shield className="w-32 h-32 text-violet-600" />
          </div>
          <div className="absolute bottom-20 left-20 animate-float animation-delay-2000">
            <Lock className="w-24 h-24 text-violet-500" />
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-white mb-6 leading-tight uppercase" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 900 }}>
              Własność Intelektualna i Ochrona Danych Osobowych
            </h1>
            <p className="text-2xl lg:text-3xl text-violet-500 mb-8 font-semibold">
              Bezpieczeństwo Twojego Majątku i Informacji
            </p>
            <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
              Wartość Twojej firmy to nie tylko aktywa materialne, ale także własność intelektualna i dane.
            </p>
            <p className="text-lg text-gray-400 mb-12 max-w-4xl mx-auto">
              Szynkowski Legal oferuje kompleksowe doradztwo w zakresie ochrony Twoich innowacji, marek i przestrzegania przepisów o ochronie danych osobowych, gwarantując spokój i bezpieczeństwo.
            </p>
            
            <div className="flex justify-center">
              <button 
                onClick={handleContactScroll}
                className="group flex items-center gap-3 text-white text-xl lg:text-2xl font-medium hover:text-violet-500 transition-all duration-300 uppercase"
              >
                <span>Bezpłatna konsultacja</span>
                <ArrowRight className="w-6 h-6 text-violet-600 group-hover:text-violet-500 group-hover:translate-x-2 group-hover:scale-110 transition-all duration-300" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-16 relative">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black text-white mb-6 md:mb-8 uppercase leading-tight" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 900 }}>
              Kompleksowe doradztwo w zakresie własności intelektualnej i ochrony danych osobowych
            </h2>
            <p className="text-lg text-gray-300">
              W dzisiejszej gospodarce innowacje, marki i dane stanowią kluczowe aktywa każdej firmy. Ich prawidłowa ochrona jest niezbędna dla utrzymania przewagi konkurencyjnej i uniknięcia kosztownych sporów. W Szynkowski Legal zapewniamy pełne wsparcie prawne w zakresie prawa własności intelektualnej oraz bieżące doradztwo w obszarze RODO.
            </p>
          </div>
        </div>
      </section>

      {/* Intellectual Property Services Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl lg:text-5xl font-black text-white mb-4 uppercase leading-tight" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 900 }}>
              Własność intelektualna – zakres usług
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {intellectualPropertyServices.map((service, index) => (
              <div 
                key={index}
                className="group backdrop-blur-md bg-white/5 rounded-xl p-8 border border-white/10 hover:border-violet-600/50 transition-all duration-300 hover:shadow-xl hover:shadow-violet-600/10"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-violet-600/20 to-violet-700/20 rounded-lg flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <span className="text-violet-600">
                    {service.icon}
                  </span>
                </div>
                <h3 className="text-xl font-bold text-white mb-3">
                  {service.title}
                </h3>
                <p className="text-gray-400">
                  {service.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* RODO Services Section */}
      <section className="py-16 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-violet-600/5 to-transparent"></div>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl lg:text-5xl font-black text-white mb-4 uppercase leading-tight" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 900 }}>
              Ochrona danych osobowych (RODO) – zakres usług
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {rodoServices.map((service, index) => (
              <div 
                key={index}
                className="group backdrop-blur-md bg-white/5 rounded-xl p-8 border border-white/10 hover:border-violet-600/50 transition-all duration-300 hover:shadow-xl hover:shadow-violet-600/10"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-violet-600/20 to-violet-700/20 rounded-lg flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <span className="text-violet-600">
                    {service.icon}
                  </span>
                </div>
                <h3 className="text-xl font-bold text-white mb-3">
                  {service.title}
                </h3>
                <p className="text-gray-400">
                  {service.description}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <p className="text-xl text-gray-300 mb-8">
              Chroń to, co najcenniejsze w Twojej firmie. Skontaktuj się z nami po profesjonalne doradztwo w zakresie własności intelektualnej i RODO!
            </p>
            <button 
              onClick={handleContactScroll}
              className="group flex items-center gap-3 text-white text-xl lg:text-2xl font-medium hover:text-violet-500 transition-all duration-300 uppercase mx-auto"
            >
              <span>Bezpłatna konsultacja</span>
              <ArrowRight className="w-6 h-6 text-violet-600 group-hover:text-violet-500 group-hover:translate-x-2 group-hover:scale-110 transition-all duration-300" />
            </button>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <h2 className="text-3xl lg:text-5xl font-black text-white mb-12 text-center uppercase" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 900 }}>
            FAQ
          </h2>
          
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div 
                key={index}
                className="backdrop-blur-md bg-white/5 rounded-xl border border-white/10 overflow-hidden transition-all duration-300 hover:border-violet-600/30"
              >
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full px-6 py-4 text-left flex items-center justify-between group"
                >
                  <h3 className="text-lg font-semibold text-white pr-4">
                    {faq.question}
                  </h3>
                  {openFaq === index ? (
                    <ChevronUp className="w-5 h-5 text-violet-600 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400 group-hover:text-violet-600 transition-colors flex-shrink-0" />
                  )}
                </button>
                
                <div className={`transition-all duration-300 ${openFaq === index ? 'max-h-96' : 'max-h-0'} overflow-hidden`}>
                  <div className="px-6 pb-4">
                    <p className="text-gray-400">
                      {faq.answer}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <Trust />

      {/* Contact Form */}
      <ContactForm />

      {/* Animation styles */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-30px) rotate(10deg); }
        }
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        .animation-delay-2000 { animation-delay: 2s; }
      `}</style>
    </div>
  );
};

export default WlasnoscIntelektualna;